import React, { useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'src/utils/dayjs';
import {
  Box,
  Button,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import { getEquipmentLabel } from 'src/utils/equipments';
import { useTranslation } from 'react-i18next';
import SearchBar from 'src/components/SearchBar';
import { InvoicePaymentStatusFilter } from 'src/utils/constants/transactions';
import CutoffDateDialog from './CutoffDateDialog';
import useStyles from './styles';

const CONTRACT_FOLIO_PREFIX_PATTERN = /^(NOR|MSI|PFI)\s*-\s*/i;

const stripContractFolioPrefix = (folio) =>
  folio ? folio.replace(CONTRACT_FOLIO_PREFIX_PATTERN, '') : folio;

const TransactionsToolbar = ({
  folio,
  onFolioChange,
  contracts,
  contractId,
  onContractChange,
  showContractFilter,
  paymentStatus,
  onPaymentStatusChange,
  cutoffDate,
  onApplyCutoffDate,
  onDownload
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [cutoffDialogOpen, setCutoffDialogOpen] = useState(false);

  const handleStatusChange = (_, value) => {
    if (value === null) return;
    onPaymentStatusChange(value);
  };

  return (
    <Box className={classes.toolbar}>
      <Box className={classes.toolbarLeft}>
        <SearchBar
          searchValue={folio}
          handleSearch={(event) => onFolioChange(event.target.value)}
          placeholder={t('TRANSACTION.SEARCH_BY_FOLIO_PLACEHOLDER')}
          sx={{ ml: -2, width: 310 }}
        />
        {showContractFilter && (
          <Select
            className={classes.toolbarContract}
            value={contractId || ''}
            onChange={(event) => onContractChange(event.target.value)}
            displayEmpty
            renderValue={(value) => {
              if (!value) return t('TRANSACTION.ALL_CONTRACTS');
              const selected = contracts.find((c) => c.id === value);
              return stripContractFolioPrefix(selected?.folio) ?? '';
            }}
          >
            <MenuItem value="">{t('TRANSACTION.ALL_CONTRACTS')}</MenuItem>
            {contracts.map((contract) => {
              const equipmentLabel = getEquipmentLabel(contract.equipment);
              return (
                <MenuItem key={contract.id} value={contract.id}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography>
                      {stripContractFolioPrefix(contract.folio)}
                    </Typography>
                    {equipmentLabel && (
                      <Typography variant="caption" color="text.secondary">
                        {equipmentLabel}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        )}
        <ToggleButtonGroup
          className={classes.statusFilters}
          value={paymentStatus}
          exclusive
          onChange={handleStatusChange}
        >
          <ToggleButton
            className={classes.statusChip}
            value={InvoicePaymentStatusFilter.ALL}
          >
            {t('TRANSACTION.STATUS_FILTER_ALL')}
          </ToggleButton>
          <ToggleButton
            className={classes.statusChip}
            value={InvoicePaymentStatusFilter.PENDING}
          >
            {t('TRANSACTION.STATUS_FILTER_PENDING')}
          </ToggleButton>
          <ToggleButton
            className={classes.statusChip}
            value={InvoicePaymentStatusFilter.OVERDUE}
          >
            {t('TRANSACTION.STATUS_FILTER_OVERDUE')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box className={classes.toolbarRight}>
        <Button
          className={classes.cutoffButton}
          color="primary"
          onClick={() => setCutoffDialogOpen(true)}
        >
          <Box component="span" className={classes.cutoffButtonLabel}>
            {t('TRANSACTION.CUTOFF_DATE')}
          </Box>
          <Box component="span" className={classes.cutoffButtonValue}>
            {(cutoffDate || dayjs.tz()).format('DD MMM YYYY')}
          </Box>
        </Button>
        <Button
          className={classes.downloadButton}
          color="primary"
          startIcon={<GetAppIcon />}
          onClick={onDownload}
        >
          {t('COMMON.DOWNLOAD')}
        </Button>
      </Box>
      <CutoffDateDialog
        open={cutoffDialogOpen}
        cutoffDate={cutoffDate}
        handleClose={() => setCutoffDialogOpen(false)}
        handleApply={(value) => {
          onApplyCutoffDate(value);
          setCutoffDialogOpen(false);
        }}
      />
    </Box>
  );
};

TransactionsToolbar.propTypes = {
  folio: PropTypes.string.isRequired,
  onFolioChange: PropTypes.func.isRequired,
  contracts: PropTypes.array,
  contractId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onContractChange: PropTypes.func,
  showContractFilter: PropTypes.bool,
  paymentStatus: PropTypes.string.isRequired,
  onPaymentStatusChange: PropTypes.func.isRequired,
  cutoffDate: PropTypes.object,
  onApplyCutoffDate: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired
};

TransactionsToolbar.defaultProps = {
  contracts: [],
  contractId: '',
  onContractChange: () => {},
  showContractFilter: true,
  cutoffDate: null
};

export default TransactionsToolbar;
